import React from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InventoryIcon from "@mui/icons-material/Inventory";
import promoterImage from "../../components/assets/whoPromotes/whoPromotes.png";
import techtalkImage from "../../components/assets/whoPromotes/techtalk.jpeg";
import selenaImage from "../../components/assets/whoPromotes/selena.jpeg";

/* ——— Netflix-ish tokens ——— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 16;

/* ——— Section wrapper ——— */
const SectionRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
  background: COLORS.bg,
  fontFamily: "Lexend, sans-serif",
  padding: theme.spacing(10, 2),
  overflow: "hidden",
  // subtle vignette & soft glows
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(900px 450px at 12% 12%, rgba(229,9,20,0.14), transparent 60%), radial-gradient(800px 420px at 85% 10%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: "none",
  },
}));

const MotionBox = motion(Box);

/* ——— Image tiles ——— */
const ImageCard = styled(Box)(() => ({
  textAlign: "center",
}));

const ImageWrapper = styled(motion.div)(({ theme }) => ({
  width: "100%",
  maxWidth: 320,
  aspectRatio: "1/1",
  margin: "0 auto",
  borderRadius: RADIUS,
  overflow: "hidden",
  background: COLORS.panel,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const TileImage = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
}));

/* ——— Feature cards ——— */
const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.25),
  borderRadius: RADIUS,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.75),
  backgroundColor: COLORS.panel,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
  transition: "border-color 180ms ease, transform 220ms ease",
  minHeight: 120,
  "&:hover": {
    borderColor: COLORS.red,
    transform: "translateY(-2px)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
  color: COLORS.text,
  display: "flex",
  alignItems: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(229,9,20,0.18)",
    filter: "blur(8px)",
    left: -4,
    top: -4,
    zIndex: 0,
  },
  "& .MuiSvgIcon-root": {
    position: "relative",
    zIndex: 1,
  },
}));

const WhoPromotesSection: React.FC = () => {
  const features = [
    {
      icon: <EmojiObjectsIcon fontSize="large" />,
      label: "Sponsor live talks, performances, or workshops.",
    },
    {
      icon: <LocalOfferIcon fontSize="large" />,
      label: "Collaborate with talents to run awareness or CSR campaigns.",
    },
    {
      icon: <InventoryIcon fontSize="large" />,
      label: "Launch products through authentic, real-time engagement.",
    },
  ];

  const imageTiles = [
    {
      src: techtalkImage,
      alt: "Tech Talk",
      label: "Empowering Women in Technology",
    },
    {
      src: selenaImage,
      alt: "Selena Gomez",
      label: "Selena Gomez's Unicef Charity Concert Raises Over $200K",
    },
    {
      src: promoterImage,
      alt: "Sponsor Performance",
      label: "Brand-Sponsored Live Performance Connects Artists and Audiences",
    },
  ];

  return (
    <SectionRoot>
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 6 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Heading */}
          <Typography
            variant="h4"
            fontWeight={900}
            mb={1}
            sx={{ color: COLORS.text, letterSpacing: "-0.02em" }}
          >
            WHO PROMOTES
          </Typography>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: COLORS.textMuted }}
            mb={3}
          >
            For Corporate Members: Brands, NGOs, Associations, and More
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: COLORS.textMuted, maxWidth: 900, mb: 6 }}
          >
            <Box component="span" sx={{ color: COLORS.red, fontWeight: 800 }}>
              StageLink enables immersive and interactive brand experiences.
            </Box>
            <br />
            StageLink allows your brand or organization to:
          </Typography>

          {/* Promo/Image tiles */}
          <Grid container spacing={3} justifyContent="center" mb={6}>
            {imageTiles.map((tile, idx) => (
              <Grid item xs={12} sm={4} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <ImageCard>
                    <ImageWrapper
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 0.25 }}
                    >
                      <TileImage src={tile.src} alt={tile.alt} />
                    </ImageWrapper>
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      mt={2}
                      sx={{ color: COLORS.text }}
                    >
                      {tile.label}
                    </Typography>
                  </ImageCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Feature Cards */}
          <Grid container spacing={2.5}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex" }}>
                <motion.div
                  style={{ display: "flex", width: "100%" }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <StyledCard>
                    <IconWrapper>{feature.icon}</IconWrapper>
                    <Typography
                      variant="body1"
                      sx={{ color: COLORS.textMuted, fontWeight: 600 }}
                    >
                      {feature.label}
                    </Typography>
                  </StyledCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <Box mt={6} maxWidth={900}>
            <Typography variant="body1" sx={{ color: COLORS.textMuted }}>
              ✨ From NGOs to global brands, bring your initiatives to life with
              real human connection — at scale.
            </Typography>
          </Box>
        </MotionBox>
      </Container>
    </SectionRoot>
  );
};

export default WhoPromotesSection;
